export type TranslationStatusEntity = {
  hasMissingTranslations?: boolean;
  hasStaleTranslations?: boolean;
};

export type TranslationSubitem = TranslationStatusEntity & {
  items?: TranslationSubitem[];
  subitems?: TranslationSubitem[];
};

export type TranslationSequence = TranslationStatusEntity & {
  id?: number;
  templateName?: string;
  narration?: boolean;
  items?: TranslationSubitem[];
  subitems?: TranslationSubitem[];
};

export type TranslationChapterSequence = {
  sequence?: TranslationSequence;
};

export type TranslationChapter = TranslationStatusEntity & {
  chapterSequences?: TranslationChapterSequence[];
};

export type TranslationTraining = TranslationStatusEntity & {
  id?: number;
  companyId?: number | string;
  status?: 'live' | 'draft' | string;
  shouldDisplayTranslateButton?: boolean;
  companyFeatures?: {
    courseAuthoring?: boolean;
  };
  canAssignLearners?: boolean;
  chapters?: TranslationChapter[];
};

export const hasDirectTranslationGaps = (
  entity: TranslationStatusEntity | null | undefined
): boolean => {
  return Boolean(
    entity?.hasMissingTranslations || entity?.hasStaleTranslations
  );
};

const hasSubitemTranslationGaps = (
  subitems: TranslationSubitem[] = []
): boolean => {
  return subitems.some(subitem => {
    if (hasDirectTranslationGaps(subitem)) return true;
    return hasSubitemTranslationGaps(subitem.items || subitem.subitems || []);
  });
};

export const hasTrainingTranslationGaps = (
  training: TranslationTraining | null | undefined
): boolean => {
  if (!training) return false;
  if (hasDirectTranslationGaps(training)) return true;

  return (training.chapters || []).some(chapter => {
    if (hasDirectTranslationGaps(chapter)) return true;

    return (chapter.chapterSequences || []).some(chapterSequence => {
      const sequence = chapterSequence.sequence;
      if (!sequence) return false;

      if (hasDirectTranslationGaps(sequence)) return true;
      return hasSubitemTranslationGaps(
        sequence.items || sequence.subitems || []
      );
    });
  });
};

export const getTrainingSequences = (
  training: TranslationTraining | null | undefined
): Array<TranslationSequence & { id: number }> => {
  return (training?.chapters || [])
    .flatMap(chapter => chapter.chapterSequences || [])
    .map(chapterSequence => chapterSequence.sequence)
    .filter((sequence): sequence is TranslationSequence & { id: number } =>
      Boolean(sequence && typeof sequence.id === 'number')
    );
};
